import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, MessageSquare, Menu, MapPin } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { PHONE_TEL, ADDRESS_LINE1, ADDRESS_LINE2 } from "../data/site";
import { CATEGORIES } from "../data/services";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Treatments", to: "/treatments", menu: true },
  { label: "About", to: "/about" },
  { label: "Results", to: "/#results" },
  { label: "Journal", to: "/blog" },
  { label: "Memberships", to: "/memberships" },
  { label: "Contact Us", to: "/contact-us" },
  { label: "New Client Offer", to: "/new-client-offer" },
  { label: "Book Online", to: "/book-online" },
];

const UtilityBar = () => (
  <div className="bg-neutral-950 text-white" data-testid="utility-bar">
    <div className="mx-auto flex h-10 max-w-[1400px] items-center justify-between px-4 md:px-8">
      <span className="hidden items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-neutral-300 sm:flex">
        <MapPin size={12} strokeWidth={1.5} /> {ADDRESS_LINE1}, {ADDRESS_LINE2}
      </span>
      <div className="ml-auto flex items-center gap-5 text-[10px] uppercase tracking-[0.25em]">
        <a href={`tel:${PHONE_TEL}`} data-testid="utility-call-link" className="flex items-center gap-1.5 text-neutral-300 transition-colors hover:text-white">
          <Phone size={12} strokeWidth={1.5} /> Call
        </a>
        <a href={`sms:${PHONE_TEL}`} data-testid="utility-text-link" className="flex items-center gap-1.5 text-neutral-300 transition-colors hover:text-white">
          <MessageSquare size={12} strokeWidth={1.5} /> Text
        </a>
        <Link to="/book-online" data-testid="utility-book-now-btn" className="bg-white px-4 py-1.5 font-medium text-neutral-950 transition-colors hover:bg-neutral-300">
          Book Now
        </Link>
      </div>
    </div>
  </div>
);

const TreatmentsMenu = () => (
  <div className="invisible absolute left-1/2 top-full z-40 w-[720px] -translate-x-1/2 border border-neutral-200 bg-white p-10 opacity-0 transition-opacity duration-300 group-hover:visible group-hover:opacity-100">
    <div className="grid grid-cols-2 gap-x-12 gap-y-4">
      {CATEGORIES.map((c) => (
        <Link key={c.id} to={`/treatments#${c.id}`} data-testid={`nav-menu-${c.id}`} className="font-display text-xl text-neutral-900 transition-colors hover:text-neutral-500">
          {c.name}
        </Link>
      ))}
    </div>
  </div>
);

const MobileNav = () => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="lg:hidden" aria-label="Open menu" data-testid="mobile-menu-btn">
          <Menu strokeWidth={1.25} />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-80 bg-white">
        <SheetTitle className="kicker mb-10">Menu</SheetTitle>
        <nav className="flex flex-col gap-6">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} className="font-display text-3xl text-neutral-950">
              {n.label}
            </Link>
          ))}
          <Link to="/book-online" onClick={() => setOpen(false)} className="btn-ink mt-6" data-testid="mobile-book-btn">Book Now</Link>
        </nav>
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => (
  <header className="sticky top-0 z-50 bg-white" data-testid="site-header">
    <UtilityBar />
    <div className="border-b border-neutral-200">
      <div className="relative mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 md:px-8 lg:justify-center">
        <MobileNav />
        <Link to="/" onClick={() => window.scrollTo(0, 0)} data-testid="header-logo-link" className="block">
          <img src="/logo-black.png" alt="Lux Veritae Skin Spa" className="h-20 w-auto md:h-24" />
        </Link>
        <Link to="/book-online" className="text-[10px] uppercase tracking-[0.25em] lg:hidden" data-testid="header-mobile-book">Book</Link>
      </div>
      <nav className="hidden justify-center gap-5 pb-4 lg:flex xl:gap-10" data-testid="main-nav">
        {NAV.map((n) => (
          <div key={n.label} className="group relative">
            <NavLink to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} className="text-[11px] font-medium uppercase tracking-[0.25em] text-neutral-900 transition-colors hover:text-neutral-500">
              {n.label}
            </NavLink>
            {n.menu && <TreatmentsMenu />}
          </div>
        ))}
      </nav>
    </div>
  </header>
);
